/*
 * Application Insights JavaScript SDK - Click Analytics, 2.8.18
 * Copyright (c) Microsoft and contributors. All rights reserved.
 */
/**
* @copyright Microsoft 2020
*/
import { __extendsFn as __extends } from "@microsoft/applicationinsights-shims";
import dynamicProto from "@microsoft/dynamicproto-js";
import { PropertiesPluginIdentifier } from "@microsoft/applicationinsights-common";
import { BaseTelemetryPlugin, _throwInternal, arrForEach, dumpObj, getExceptionName, isNullOrUndefined, strTrim, throwError, unloadComponents } from "@microsoft/applicationinsights-core-js";
import { _DYN_EXTENSION_CONFIG, _DYN_LENGTH, _DYN_PAGE_ACTION_PAGE_TAG0, _DYN_TRACK_PAGE_ACTION } from "./__DynamicConstants";
import { BehaviorEnumValidator, BehaviorMapValidator, BehaviorValueValidator, mergeConfig } from "./common/Utils";
import { PageAction } from "./events/PageAction";
import { AutoCaptureHandler } from "./handlers/AutoCaptureHandler";
import { DomContentHandler } from "./handlers/DomContentHandler";
export { BehaviorMapValidator, BehaviorValueValidator, BehaviorEnumValidator };
var ClickAnalyticsPlugin = /** @class */ (function (_super) {
    __extends(ClickAnalyticsPlugin, _super);
    function ClickAnalyticsPlugin() {
        var _this = _super.call(this) || this;
        _this.identifier = "ClickAnalyticsPlugin";
        _this.priority = 181;
        var _config;
        var _pageAction;
        var _autoCaptureHandler;
        var _contentHandler;
        dynamicProto(ClickAnalyticsPlugin, _this, function (_self, _base) {
            var _identifier = _self.identifier;
            _initDefaults();
            _self.initialize = function (config, core, extensions, pluginChain) {
                if (isNullOrUndefined(core)) {
                    throwError("Error initializing");
                }
                config[_DYN_EXTENSION_CONFIG /* @min:%2eextensionConfig */] = config[_DYN_EXTENSION_CONFIG /* @min:%2eextensionConfig */] || [];
                config[_DYN_EXTENSION_CONFIG /* @min:%2eextensionConfig */][_identifier] = config[_DYN_EXTENSION_CONFIG /* @min:%2eextensionConfig */][_identifier] || {};
                _config = mergeConfig(config[_DYN_EXTENSION_CONFIG /* @min:%2eextensionConfig */][_identifier]);
                _super.prototype.initialize.call(_this, config, core, extensions, pluginChain);
                var logger = _self.diagLog();
                // Default to DOM content handler
                _contentHandler = _contentHandler ? _contentHandler : new DomContentHandler(_config, logger);
                var metaTags = _contentHandler.getMetadata();
                _pageAction = new PageAction(_self, _config, _contentHandler, _config.callback[_DYN_PAGE_ACTION_PAGE_TAG0 /* @min:%2epageActionPageTags */], metaTags, logger);
                // Default to DOM autoCapture handler
                _autoCaptureHandler = _autoCaptureHandler ? _autoCaptureHandler : new AutoCaptureHandler(_self, _config, _pageAction, logger);
                if (_config.autoCapture) {
                    _autoCaptureHandler.click();
                }
                // Find the properties plugin.
                var _propertiesExtension;
                arrForEach(extensions, function (extension) {
                    if (extension.identifier === PropertiesPluginIdentifier) {
                        _propertiesExtension = extension;
                    }
                });
                // Append Click Analytics Plugin Version to SDK version.
                if (_propertiesExtension && _propertiesExtension.context && _propertiesExtension.context.internal) {
                    var theVersion = _propertiesExtension.context.internal.sdkVersion;
                    if (theVersion) {
                        theVersion += "_ClickPlugin" + ClickAnalyticsPlugin.Version;
                        if (theVersion[_DYN_LENGTH /* @min:%2elength */] > 64) {
                            theVersion = strTrim(theVersion.substring(0, 64));
                        }
                        _propertiesExtension.context.internal.sdkVersion = theVersion;
                    }
                }
            };
            _self.processTelemetry = function (env, itemCtx) {
                _self.processNext(env, itemCtx);
            };
            _self[_DYN_TRACK_PAGE_ACTION /* @min:%2etrackPageAction */] = function (pageAction, customProperties) {
                try {
                    _pageAction[_DYN_TRACK_PAGE_ACTION /* @min:%2etrackPageAction */](pageAction, customProperties);
                }
                catch (e) {
                    _throwInternal(_self.diagLog(), 1 /* eLoggingSeverity.CRITICAL */, 103 /* _eInternalMessageId.TrackPageActionEventFailed */, "trackPageAction failed, page action event will not be collected: " + getExceptionName(e), { exception: dumpObj(e) });
                }
            };
            _self._doTeardown = function (unloadCtx, unloadState, asyncCallback) {
                return unloadComponents([
                    _autoCaptureHandler,
                    _contentHandler,
                    _pageAction
                ], unloadCtx, unloadState, function () {
                    _initDefaults();
                    asyncCallback && asyncCallback();
                });
            };
        });
        function _initDefaults() {
            _config = null;
            _pageAction = null;
            _autoCaptureHandler = null;
            _contentHandler = null;
        }
        return _this;
    }
// Removed Stub for ClickAnalyticsPlugin.prototype.initialize.
// Removed Stub for ClickAnalyticsPlugin.prototype.processTelemetry.
// Removed Stub for ClickAnalyticsPlugin.prototype.trackPageAction.
    ClickAnalyticsPlugin.Version = '2.8.18';
    return ClickAnalyticsPlugin;
}(BaseTelemetryPlugin));
export { ClickAnalyticsPlugin };
//# sourceMappingURL=ClickAnalyticsPlugin.js.map